<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, computed, watch } from "vue";
import VueApexCharts from "vue3-apexcharts";
import api from "@/services/api";
import { messages } from "@/i18n"; // ✅ يستخدم ملفات en/ar اللي عندك (flat keys)

// ✅ local component
const apexchart = VueApexCharts;

// ===== lang handling (same pattern you use) =====
const normalizeLang = (raw) =>
  String(raw || "").toLowerCase().startsWith("en") ? "en" : "ar";

const lang = ref(
  normalizeLang(
    document.documentElement.lang || localStorage.getItem("app_lang") || "ar"
  )
);

const isAr = computed(() => lang.value === "ar");
let observer = null;

// ===== translator (flat keys + vars) =====
const getMessage = (key) => {
  const base = messages?.[lang.value];
  if (!base) return null;
  return base[key] ?? null;
};

const tr = (key, vars = null) => {
  const val = getMessage(key);
  if (val == null) return key;

  if (vars && typeof val === "string") {
    return val.replace(/\{(\w+)\}/g, (_, k) => (vars?.[k] ?? `{${k}}`));
  }
  return val;
};

// ===== props =====
const props = defineProps({
  height: { type: [Number, String], default: 320 },
  year: { type: Number, default: new Date().getFullYear() },
});

// ===== year selector =====
const currentYear = new Date().getFullYear();
const years = computed(() => [currentYear, currentYear - 1, currentYear - 2, currentYear - 3]);
const selectedYear = ref(props.year);

watch(
  () => props.year,
  (y) => {
    if (Number(y) && selectedYear.value !== Number(y)) selectedYear.value = Number(y);
  }
);

// ===== UI state =====
const loading = ref(false);

// ===== months (from your translation files) =====
const months = computed(() => [
  tr("month_jan"),
  tr("month_feb"),
  tr("month_mar"),
  tr("month_apr"),
  tr("month_may"),
  tr("month_jun"),
  tr("month_jul"),
  tr("month_aug"),
  tr("month_sep"),
  tr("month_oct"),
  tr("month_nov"),
  tr("month_dec"),
]);

// ===== helpers =====
function normalize12(arr) {
  const out = Array(12).fill(0);
  for (let i = 0; i < Math.min(arr?.length ?? 0, 12); i++) out[i] = Number(arr[i] ?? 0);
  return out;
}

function calcMaxY(seriesArr) {
  const all = seriesArr.flatMap((s) => s?.data || []);
  const m = Math.max(0, ...all);
  const step = m <= 10 ? 2 : 10;
  return Math.max(10, Math.ceil(m / step) * step);
}

// ===== brand colors (website identity) =====
const chartColors = {
  chat: "#0f7f74",     // teal
  session: "#c9a23a",  // gold
  finished: "#166534", // green
};

const labelColor = "rgba(100, 116, 139, 1)";
const headingColor = "rgba(15, 23, 42, 1)";
const borderColor = "rgba(231, 238, 247, 1)";

// ===== series =====
const series = ref([
  { name: tr("dashboard_consultations_series_live_chat"), type: "column", data: Array(12).fill(0) },
  { name: tr("dashboard_consultations_series_sessions"), type: "column", data: Array(12).fill(0) },
  { name: tr("dashboard_consultations_series_finished"), type: "line", data: Array(12).fill(0) },
]);

// ===== chart options (stable) =====
const consultingConfig = ref({
  chart: {
    type: "line",
    stacked: false,
    parentHeightOffset: 0,
    toolbar: { show: false },
    zoom: { enabled: false },
    animations: { enabled: true },
  },

  colors: [chartColors.chat, chartColors.session, chartColors.finished],

  markers: {
    size: [0, 0, 5],
    colors: ["#fff"],
    strokeColors: chartColors.finished,
    strokeWidth: 3,
    hover: { size: 6 },
  },

  stroke: {
    curve: "smooth",
    width: [0, 0, 3],
    lineCap: "round",
  },

  plotOptions: {
    bar: {
      columnWidth: "36%",
      borderRadius: 8,
      borderRadiusApplication: "end",
    },
  },

  grid: { strokeDashArray: 8, borderColor },

  dataLabels: { enabled: false },

  legend: {
    show: true,
    position: "bottom",
    fontSize: "13px",
    fontFamily: "Cairo, Tajawal, system-ui",
    fontWeight: 800,
    labels: { colors: headingColor },
    markers: { width: 10, height: 10, offsetX: -3 },
    itemMargin: { horizontal: 10, vertical: 6 },
  },

  xaxis: {
    categories: months.value,
    labels: {
      style: {
        colors: Array(12).fill(labelColor),
        fontSize: "12px",
        fontWeight: 700,
        fontFamily: "Cairo, Tajawal, system-ui",
      },
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },

  yaxis: {
    min: 0,
    max: 10,
    tickAmount: 4,
    labels: {
      style: { colors: labelColor, fontSize: "12px", fontWeight: 700 },
      formatter(val) {
        return `${Math.round(val)}`;
      },
    },
  },

  tooltip: {
    shared: true,
    intersect: false,
    y: {
      formatter(val) {
        return tr("dashboard_consultations_tooltip_count", { count: Math.round(val) });
      },
    },
  },

  responsive: [
    {
      breakpoint: 768,
      options: {
        plotOptions: { bar: { columnWidth: "44%" } },
        legend: { fontSize: "12px" },
      },
    },
  ],
});

// ===== update config + labels on lang change =====
watch(
  () => lang.value,
  async () => {
    // months + tooltip
    consultingConfig.value = {
      ...consultingConfig.value,
      xaxis: { ...consultingConfig.value.xaxis, categories: months.value },
      tooltip: {
        ...consultingConfig.value.tooltip,
        y: {
          formatter(val) {
            return tr("dashboard_consultations_tooltip_count", { count: Math.round(val) });
          },
        },
      },
      legend: {
        ...consultingConfig.value.legend,
        fontFamily: "Cairo, Tajawal, system-ui",
      },
    };

    // series names (keep data)
    series.value = [
      { ...series.value[0], name: tr("dashboard_consultations_series_live_chat") },
      { ...series.value[1], name: tr("dashboard_consultations_series_sessions") },
      { ...series.value[2], name: tr("dashboard_consultations_series_finished") },
    ];

    await nextTick();
  },
  { immediate: true }
);

// ===== update y-axis max when data changes =====
watch(
  series,
  async (s) => {
    consultingConfig.value = {
      ...consultingConfig.value,
      yaxis: { ...consultingConfig.value.yaxis, max: calcMaxY(s) },
    };
    await nextTick();
  },
  { deep: true, immediate: true }
);

// ===== API fetch =====
async function fetchYearlyConsultations() {
  loading.value = true;
  try {
    const year = Number(selectedYear.value || currentYear);
    const res = await api.get("/consultationsYearlyOverview", { params: { year } });

    const json = res?.data ?? {};
    const finished = normalize12(json?.series?.finished);
    const chat = normalize12(json?.series?.chat);
    const session = normalize12(json?.series?.session);

    series.value = [
      { name: tr("dashboard_consultations_series_live_chat"), type: "column", data: chat },
      { name: tr("dashboard_consultations_series_sessions"), type: "column", data: session },
      { name: tr("dashboard_consultations_series_finished"), type: "line", data: finished },
    ];
  } catch (e) {
    console.error("Failed to fetch yearly consultations:", e);
    // keep stable
    series.value = [
      { name: tr("dashboard_consultations_series_live_chat"), type: "column", data: Array(12).fill(0) },
      { name: tr("dashboard_consultations_series_sessions"), type: "column", data: Array(12).fill(0) },
      { name: tr("dashboard_consultations_series_finished"), type: "line", data: Array(12).fill(0) },
    ];
  } finally {
    loading.value = false;
  }
}

watch(selectedYear, fetchYearlyConsultations);

const headerMeta = computed(() =>
  loading.value ? tr("common_updating") : tr("dashboard_year", { year: selectedYear.value })
);

onMounted(() => {
  fetchYearlyConsultations();

  observer = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.attributeName === "lang") {
        lang.value = normalizeLang(document.documentElement.lang || "ar");
      }
    }
  });
  observer.observe(document.documentElement, { attributes: true });
});

onBeforeUnmount(() => {
  if (observer) observer.disconnect();
});
</script>

<template>
  <section class="brandChart mt-4" :dir="isAr ? 'rtl' : 'ltr'">
    <header class="brandChart__head">
      <div class="brandChart__left">
        <div class="brandChart__kicker">
          <span class="dot" aria-hidden="true"></span>
          <span>{{ tr("dashboard_consultations_overview_title") }}</span>
        </div>
        <p class="brandChart__sub">{{ tr("dashboard_consultations_overview_subtitle") }}</p>
      </div>

      <!-- <div class="brandChart__right">
        <span class="brandChart__meta">{{ headerMeta }}</span>

        <label class="brandChart__selectWrap">
          <span class="sr-only">Year</span>
          <select v-model.number="selectedYear" class="brandChart__select">
            <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
          </select>
          <i class="mdi mdi-chevron-down brandChart__chev" aria-hidden="true"></i>
        </label>
      </div> -->
    </header>

    <div class="brandChart__body ">
      <apexchart
        :options="consultingConfig"
        :series="series"
        type="line"
        :height="Number(props.height)"
      />
    </div>
  </section>
</template>

<style scoped>
/* ✅ matches website identity (teal + gold + soft shapes) + degree color */
.brandChart {
  --border: #e7eef7;
  --surface: #fff;
  --text: #0f172a;
  --muted: #64748b;

  --teal: #0f7f74;
  --gold: #c9a23a;

  /* requested primary degree */
  --degree: #cdeffb87;

  border: 1px solid var(--border);
  border-radius: 18px;
  background:
    radial-gradient(900px 280px at 85% 0%, var(--degree), transparent 60%),
    radial-gradient(760px 260px at 10% 100%, rgba(201, 162, 58, 0.12), transparent 55%),
    #fff;
  box-shadow: 0 14px 40px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.brandChart__head {
  padding: 14px 14px 10px;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.brandChart__left {
  min-width: 0;
}

.brandChart__kicker {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 1000;
  color: var(--teal);
  font-size: 13px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(15, 127, 116, 0.9), rgba(15, 127, 116, 0.25));
  box-shadow: 0 0 0 5px rgba(15, 127, 116, 0.08);
}

.brandChart__sub {
  margin: 6px 0 0;
  font-size: 12px;
  font-weight: 800;
  color: var(--muted);
}

.brandChart__right {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
}

.brandChart__meta {
  font-size: 12px;
  font-weight: 900;
  color: #334155;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: #fff;
  white-space: nowrap;
}

.brandChart__selectWrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.brandChart__select {
  height: 34px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: #fff;
  padding: 0 34px 0 12px;
  font-size: 12px;
  font-weight: 900;
  color: #0f172a;
  outline: none;
  appearance: none;
  min-width: 96px;
}
.brandChart__select:focus {
  box-shadow: 0 0 0 3px rgba(15, 127, 116, 0.16);
  border-color: rgba(15, 127, 116, 0.35);
}

.brandChart__chev {
  position: absolute;
  inset-inline-end: 10px;
  font-size: 16px;
  color: #64748b;
  pointer-events: none;
}

.brandChart__body {
  padding: 10px 12px 12px;
}

/* sr only */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
</style>
